import { frontendLogger } from './loggerCore';
import {
  captureBoundaryException,
  schedulerStepMessageLine,
  withSchedulerWorkflowContext,
  type ObservabilityContext,
} from './observability';

const SCHEDULER_BASE = { category: 'scheduler', surface: 'web' } as const;

function schedulerCtx(
  step: string,
  extra?: ObservabilityContext
): ObservabilityContext {
  return withSchedulerWorkflowContext(step, { ...SCHEDULER_BASE, ...extra });
}

function logScheduler(
  level: 'info' | 'warn' | 'error',
  headline: string,
  step: string,
  extra?: ObservabilityContext
): void {
  const ctx = schedulerCtx(step, extra);
  frontendLogger[level](schedulerStepMessageLine(headline, ctx), ctx);
}

export function schedulerStepStarted(
  step: string,
  context?: ObservabilityContext
): void {
  logScheduler('info', 'Scheduler step started', step, {
    ...context,
    result: 'started',
  });
}

export function schedulerStepCompleted(
  step: string,
  context?: ObservabilityContext
): void {
  logScheduler('info', 'Scheduler step completed', step, {
    ...context,
    result: 'success',
  });
}

export function schedulerStepFailed(
  step: string,
  error: Error | string,
  context?: ObservabilityContext
): void {
  logScheduler('error', 'Scheduler step failed', step, {
    ...context,
    error: error instanceof Error ? error.message : error,
    result: 'error',
  });
}

export function schedulerStepWarning(
  step: string,
  message: string,
  context?: ObservabilityContext
): void {
  const ctx = schedulerCtx(step, { ...context, result: 'warning' });
  frontendLogger.warn(message, ctx);
}

export function schedulerStepSkipped(
  step: string,
  reason: string,
  context?: ObservabilityContext
): void {
  logScheduler('info', 'Scheduler step skipped', step, {
    ...context,
    reason,
    result: 'skipped',
  });
}

export function schedulerStepRetried(
  step: string,
  context?: ObservabilityContext
): void {
  logScheduler('warn', 'Scheduler step retried', step, {
    ...context,
    result: 'retry',
  });
}

export function schedulerResumeDraftSkipped(
  reason: string,
  context?: ObservabilityContext
): void {
  logScheduler('info', 'Scheduler resume draft skipped', 'online_resume_draft', {
    ...context,
    reason,
    result: 'skipped',
  });
}

export function captureSchedulerFailure(
  step: string,
  error: unknown,
  context?: ObservabilityContext
): void {
  captureBoundaryException(error, withSchedulerWorkflowContext(step, context));
}

/** Same API as before; prefer named `schedulerStep*` functions for new code. */
export const schedulerLogger = {
  stepStarted: schedulerStepStarted,
  stepCompleted: schedulerStepCompleted,
  stepFailed: schedulerStepFailed,
  stepWarning: schedulerStepWarning,
  skipped: schedulerStepSkipped,
  retried: schedulerStepRetried,
  resumeDraftSkipped: schedulerResumeDraftSkipped,
  captureFailure: captureSchedulerFailure,
};
